// Angular
import { Directive, HostListener, ElementRef, Inject, Input, Renderer2, OnInit, AfterViewInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Directive({
    selector: '[sticky]',
})
export class StickyDirective implements AfterViewInit{
    @Input()
    public offset: number = 0;
    @Input()
    public stickyTriggerTopPointOffset: number = 0;

    /**
     * @input : elementToStickyAfter
     * @description : helps to target the element after what the current element'll be positionned when sticked
     */
    @Input()
    public elementToStickyAfter: string;
    @Input()
    public elementInitialPositionStrategy: 'absolute' | 'relative' = 'relative';
    @Input()
    public parentInitialPaddingTop: string;
    @Input()
    public initialWidth: string;
    @Input()
    public isKeepWidth: boolean;

    private readonly element: HTMLElement;
    private readonly window: Window;
    private readonly triggerTopPointCache: Map<string, number> = new Map();

    constructor(
        private readonly elementRef: ElementRef,
        private readonly renderer: Renderer2,
        @Inject(DOCUMENT) private readonly document: Document
    ) {
        this.element = this.elementRef.nativeElement;
        this.window = this.document.defaultView;
    }

    public ngAfterViewInit(): void {
        if(this.isKeepWidth){
            const element: HTMLElement =  this.elementRef.nativeElement;
            this.initialWidth = `${element.offsetWidth}px`;
        }
    }

    @HostListener('window:scroll', [])
    public onWindowScroll(): void {
        if (!this.hasMaterialAngularDialogResetedWindowScrollY()) {
            const parentElement: HTMLElement = this.element.parentElement;
            const elementHeight: number = this.element.offsetHeight;
            const stickyTriggerTopPoint: number = this.setStickyTriggerTopPoint();
            if ((stickyTriggerTopPoint || stickyTriggerTopPoint === 0) && this.window.scrollY > stickyTriggerTopPoint) {
                this.setTriggerTopPointCache(stickyTriggerTopPoint);
                this.renderer.setStyle(parentElement, 'padding-top', `${elementHeight + +this.parentInitialPaddingTop}px`);
                this.renderer.setStyle(this.element, 'position', 'fixed');
                this.renderer.setStyle(this.element, 'top', `${this.getOffsetTop()}px`);
                this.renderer.addClass(parentElement, 'sticky-element-container');
                this.renderer.addClass(this.element, "sticky-position");
                if (this.initialWidth) {
                    this.renderer.setStyle(this.element, 'width', this.initialWidth);
                }
            }

            if (this.triggerTopPointCache.has(this.element.id) && this.window.scrollY <= this.triggerTopPointCache.get(this.element.id)) {
                this.removeTriggerTopPointCache();
                this.renderer.setStyle(parentElement, 'padding-top', `${+this.parentInitialPaddingTop}px`);
                this.renderer.setStyle(this.element, 'position', this.elementInitialPositionStrategy);
                this.renderer.setStyle(this.element, 'top', 'unset');
                this.renderer.removeClass(parentElement, 'sticky-element-container');
                this.renderer.removeClass(this.element, "sticky-position");
                this.renderer.setStyle(this.element, 'width', 'unset');

            }

        }
    }

    /**
     * @context : material angular library has a dialog component that appends in the end of the <body> of the windows
     * (TR: 2021-10-07) => For a reason that i ignore, opening a MatDialog has the consequence of reseting the window.scrollY value...
     * This consequence implies a side effect in this directive : the sticked element disapears
     * @method hasMaterialAngularDialogResetedWindowScrollY => checks if scrollY equals to 0
     * @returns boolean
     */
    private hasMaterialAngularDialogResetedWindowScrollY(): boolean {
        return this.window.scrollY === 0;
    }

    private getOffsetTop(): number {
        if (this.elementToStickyAfter) {
            const preceedingElement: HTMLElement = this.document.getElementById(this.elementToStickyAfter);
            return preceedingElement.offsetHeight + preceedingElement.offsetTop;
        }
        return +this.offset;
    }

    private setStickyTriggerTopPoint(): number {
        if (this.elementToStickyAfter) {
            const preceedingElement: HTMLElement = this.document.getElementById(this.elementToStickyAfter);
            if (preceedingElement.offsetTop === 0) {
                return +this.element.offsetTop - (preceedingElement.offsetHeight + preceedingElement.offsetTop);
            }
            return null;
        }
        return +this.element.offsetTop - +this.stickyTriggerTopPointOffset;
    }

    private setTriggerTopPointCache(stickyTriggerTopPoint: number): void {
        if (!this.triggerTopPointCache.get(this.element.id)) {
            this.triggerTopPointCache.set(this.element.id, stickyTriggerTopPoint);
        }
    }

    private removeTriggerTopPointCache(): void {
        if (this.triggerTopPointCache.get(this.element.id)) {
            this.triggerTopPointCache.delete(this.element.id);
        }
    }
}
